
import { useToast } from '@hooks/useToast';
import { confirmRecharge, createRechargeOrder } from '@services/wallet.service';
import { useCallback, useRef, useState } from 'react';

declare global {
  interface Window {
    Razorpay?: new (options: Record<string, unknown>) => {
      open: () => void;
      on: (event: string, handler: (response: Record<string, unknown>) => void) => void;
    };
  }
}

const RAZORPAY_SCRIPT = 'https://checkout.razorpay.com/v1/checkout.js';

export function loadCheckoutScript(): Promise<void> {
  return new Promise((resolve, reject) => {
    if (window.Razorpay) {
      resolve();
      return;
    }
    const existing = document.querySelector(`script[src="${RAZORPAY_SCRIPT}"]`);
    if (existing) {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', () => reject(new Error('Could not load the payment window. Check your connection and try again.')));
      return;
    }
    const script = document.createElement('script');
    script.src = RAZORPAY_SCRIPT;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Could not load the payment window. Check your connection and try again.'));
    document.head.appendChild(script);
  });
}

/**
 * Handles the full Razorpay checkout flow:
 * 1. Creates a backend order (POST /wallets/recharge)
 * 2. Loads the Razorpay script (lazy, first use only)
 * 3. Opens the Razorpay modal
 * 4. On success → confirms payment with backend (POST /wallets/recharge/confirm)
 */
export function useRazorpay(): {
  initiate: (amount: number) => Promise<void>;
  isLoading: boolean;
  isSuccess: boolean;
  error: string | null;
} {
  const toast = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const activeRef = useRef(false);

  const initiate = useCallback(
    async (amount: number) => {
      if (activeRef.current) return;
      activeRef.current = true;
      setIsLoading(true);
      setIsSuccess(false);
      setError(null);

      try {
        const order = await createRechargeOrder(amount);
        await loadCheckoutScript();

        if (!window.Razorpay) throw new Error('Could not load the payment window. Check your connection and try again.');

        const rzp = new window.Razorpay({
          key: order.key_id,
          amount: order.amount,
          currency: order.currency,
          order_id: order.order_id,
          name: 'Witzy',
          description: `Wallet recharge ₹${amount.toLocaleString('en-IN')} + GST ₹${order.gst_amount_inr.toLocaleString('en-IN')}`,
          theme: { color: '#059669' },
          handler: async (response: Record<string, unknown>) => {
            try {
              await confirmRecharge(
                response.razorpay_payment_id as string,
                response.razorpay_order_id as string,
                response.razorpay_signature as string,
              );
              setIsSuccess(true);
              toast.success({
                title: 'Wallet recharged',
                message: `₹${amount.toLocaleString('en-IN')} added to your balance.`,
              });
            } catch {
              setError('Payment received but confirmation failed. Contact support.');
              toast.error({ title: 'Confirmation failed', message: 'Please contact support.' });
            } finally {
              setIsLoading(false);
              activeRef.current = false;
            }
          },
          modal: {
            ondismiss: () => {
              setIsLoading(false);
              activeRef.current = false;
            },
          },
        });

        rzp.open();
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Failed to start payment';
        setError(msg);
        setIsLoading(false);
        activeRef.current = false;
        toast.error({ title: 'Payment error', message: msg });
      }
    },
    [toast],
  );

  return { initiate, isLoading, isSuccess, error };
}
